/**
 * Centralized analytics — PostHog, plus the GTM data layer for the subset of
 * events the tracking spec covers.
 * Provides typed event tracking, user identification, and pre-defined helpers.
 */

import { forwardToDataLayer } from "@/shared/lib/gtm";
import type { CtaLocation, CtaName } from "@/shared/lib/gtm";
import { withPostHog } from "@/shared/lib/observability/sinks";

// posthog-js loads lazily after first paint (see observability/sinks). withPostHog
// fires immediately once it's in and queues until then, so early events (a login on
// a cold page, say) are replayed rather than dropped.

// ============================================
// Core tracking
// ============================================

/**
 * Send an event to PostHog, and to the GTM data layer when the tracking spec
 * has an equivalent for it.
 *
 * Both sinks are fed from this one call site so they cannot drift apart — see
 * shared/lib/gtm.ts for the name/parameter translation and which events cross
 * over.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>
) {
  if (typeof window === "undefined") return;
  withPostHog((posthog) => posthog.capture(event, properties));
  forwardToDataLayer(event, properties);
}

/** Track an API/feature error */
export function trackError(feature: string, error: string) {
  if (typeof window === "undefined") return;
  withPostHog((posthog) =>
    posthog.capture("api_error", { feature, error_message: error })
  );
}

// ============================================
// Identify user
// ============================================

export function identifyUser(user: {
  id: string;
  email?: string | null;
  name?: string | null;
  gender?: string | null;
  hasOnboarded?: boolean;
  creditBalance?: number;
  role?: string;
}) {
  if (typeof window === "undefined") return;
  withPostHog((posthog) =>
    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      gender: user.gender,
      has_onboarded: user.hasOnboarded,
      credit_balance: user.creditBalance,
      role: user.role || "USER",
    })
  );
}

// ============================================
// Pre-defined event helpers (typed & consistent)
// ============================================

// --- Auth & Onboarding ---

export function trackLogin(method: string) {
  trackEvent("login", { method });
}

export function trackSignUp(method: string) {
  trackEvent("sign_up", { method });
}

/**
 * The signup process was opened. Spec event `signup_start`; PostHog-side it is
 * a plain funnel entry event.
 */
export function trackSignupStart(signupMethod: string) {
  trackEvent("signup_start", { signup_method: signupMethod });
}

/**
 * A marketing CTA was clicked. The name and location come from a closed
 * vocabulary (see CtaName/CtaLocation in shared/lib/gtm.ts) so the values stay
 * groupable in GA4 instead of drifting per page.
 */
export function trackCtaClick(
  ctaName: CtaName,
  ctaLocation: CtaLocation,
  destinationUrl?: string
) {
  trackEvent("cta_click", {
    cta_name: ctaName,
    cta_location: ctaLocation,
    destination_url: destinationUrl,
  });
}

export function trackOnboardingStep(step: number, stepName?: string) {
  trackEvent("onboarding_step", { step, step_name: stepName });
}

export function trackOnboardingComplete() {
  trackEvent("onboarding_complete");
}

// --- Daily Drip ---

export function trackDailyDripStarted() {
  trackEvent("daily_drip_started");
}

export function trackDressingTypeSelected(dressingType: string) {
  trackEvent("dressing_type_selected", { dressing_type: dressingType });
}

export function trackMoodSelected(mood: string) {
  trackEvent("mood_selected", { mood });
}

export function trackDailyDripGenerated(outfitCount: number) {
  trackEvent("daily_drip_generated", { outfit_count: outfitCount });
}

export function trackOutfitViewed(outfitId?: string) {
  trackEvent("outfit_viewed", { outfit_id: outfitId });
}

// --- Wardrobe ---

export function trackWardrobeViewed(itemCount?: number) {
  trackEvent("wardrobe_viewed", { item_count: itemCount });
}

export function trackDressUploaded(method: "single" | "batch", count?: number) {
  trackEvent("dress_uploaded", { method, count: count ?? 1 });
}

// --- Wardrobe-build activation funnel ---
// The make-or-break flow for retention: ~60% of new users never build a
// wardrobe, but until now we only had a success event (dress_uploaded) — the
// per-step drop-off was invisible. These named events let us build a real
// funnel in PostHog (started → method → images_added → submitted →
// succeeded / failed) and see EXACTLY where users fall out, instead of
// inferring it. Keep the event names stable once dashboards reference them.

/** User entered the add-to-wardrobe flow. `source` = how they got in. */
export function trackWardrobeBuildStarted(source: "modal" | "camera" | "upload") {
  trackEvent("wardrobe_build_started", { source });
}

/** User picked an input method inside the flow. */
export function trackWardrobeBuildMethod(method: "camera" | "photos") {
  trackEvent("wardrobe_build_method_selected", { method });
}

/** At least one image made it into the picker (the step camera/HEIC bugs blocked). */
export function trackWardrobeBuildImagesAdded(count: number, source: "camera" | "gallery") {
  trackEvent("wardrobe_build_images_added", { count, source });
}

/** User hit submit — images leave the device for analysis. */
export function trackWardrobeBuildSubmitted(imageCount: number) {
  trackEvent("wardrobe_build_submitted", { image_count: imageCount });
}

/**
 * Items actually landed in the wardrobe (the terminal success state).
 *
 * `uploadMethod` also feeds the spec's `wardrobe_item_uploaded.upload_method`,
 * which is why it is carried on the success event rather than left on the
 * earlier `wardrobe_build_method_selected`.
 */
export function trackWardrobeBuildSucceeded(
  itemCount?: number,
  uploadMethod?: "camera" | "photos"
) {
  trackEvent("wardrobe_build_succeeded", {
    item_count: itemCount ?? 1,
    upload_method: uploadMethod,
  });
}

/**
 * The build failed. `reason` is the categorised cause so the funnel shows WHY
 * users drop, not just that they did: `no_clothing` (AI saw no garment),
 * `credits` (out of quota/coins), `error` (anything else — AI/timeout/network).
 */
export function trackWardrobeBuildFailed(
  reason: "no_clothing" | "credits" | "error",
  message?: string
) {
  trackEvent("wardrobe_build_failed", { reason, message });
}

export function trackDressDeleted() {
  trackEvent("dress_deleted");
}

export function trackDressFavorited(itemId: string, isFavorite: boolean) {
  trackEvent("dress_favorited", { item_id: itemId, is_favorite: isFavorite });
}

export function trackDressWorn(itemId: string) {
  trackEvent("dress_worn", { item_id: itemId });
}

export function trackWardrobeFiltered(filterType: string, value: string) {
  trackEvent("wardrobe_filtered", { filter_type: filterType, value });
}

export function trackWardrobeSearched(query: string) {
  trackEvent("wardrobe_searched", { search_term: query });
}

// --- Pods ---

export function trackPodCreated(podName: string) {
  trackEvent("pod_created", { pod_name: podName });
}

export function trackPodItemAdded(podId: string) {
  trackEvent("pod_item_added", { pod_id: podId });
}

export function trackPodItemRemoved(podId: string) {
  trackEvent("pod_item_removed", { pod_id: podId });
}

// --- Look Lab ---

export function trackLookLabViewed(labType: "type" | "color" | "events") {
  trackEvent("look_lab_viewed", { lab_type: labType });
}

export function trackOutfitGenerated(anchorItemId?: string) {
  trackEvent("outfit_generated", { anchor_item_id: anchorItemId });
}

export function trackOutfitLiked(outfitId: string, liked: boolean) {
  trackEvent("outfit_liked", { outfit_id: outfitId, liked });
}

// --- Shopping ---

export function trackShoppingStarted(type: "wardrobe" | "image") {
  trackEvent("shopping_started", { shopping_type: type });
}

export function trackShoppingGenerated(type: "wardrobe" | "image", productCount?: number) {
  trackEvent("shopping_generated", { shopping_type: type, product_count: productCount });
}

export function trackProductClicked(productName?: string, price?: number) {
  trackEvent("product_clicked", { product_name: productName, price });
}

// --- Style Station ---

export function trackStyleGenerated() {
  trackEvent("style_generated");
}

export function trackStyleLiked(styleId: string) {
  trackEvent("style_liked", { style_id: styleId });
}

// --- Profile ---

export function trackSkinAnalyzed() {
  trackEvent("skin_analyzed");
}

export function trackProfilePhotoUploaded() {
  trackEvent("profile_photo_uploaded");
}

export function trackTarotCardGenerated() {
  trackEvent("tarot_card_generated");
}

export function trackDailyRewardClaimed() {
  trackEvent("daily_reward_claimed");
}

// --- Billing & Payments ---

export function trackPlanViewed(planName?: string) {
  trackEvent("plan_viewed", { plan_name: planName });
}

export function trackPurchaseStarted(planName: string, price: number) {
  trackEvent("purchase_started", { plan_name: planName, price, currency: "INR" });
}

/**
 * `transactionId` is the payment provider's order ID. It is what GA4 uses to
 * recognise a purchase it has already counted, so without it a refresh of the
 * confirmation page is counted as a second sale. Optional only because older
 * call sites predate it.
 */
export function trackPurchaseCompleted(
  planName: string,
  price: number,
  credits: number,
  transactionId?: string,
  options?: { skipDataLayer?: boolean }
) {
  const properties = {
    plan_name: planName,
    price,
    credits,
    currency: "INR",
    transaction_id: transactionId,
    $revenue: price,  // PostHog revenue tracking
  };

  // PostHog always records the purchase — it is our own funnel and has no
  // server-side counterpart. GTM is skipped when the backend already reported
  // it to GA4, so the revenue is counted once, not twice.
  if (options?.skipDataLayer) {
    withPostHog((posthog) => posthog.capture("purchase_completed", properties));
    return;
  }

  trackEvent("purchase_completed", properties);
}

export function trackPurchaseFailed(planName: string, error?: string) {
  trackEvent("purchase_failed", { plan_name: planName, error });
}

export function trackCouponApplied(couponCode: string, success: boolean) {
  trackEvent("coupon_applied", { coupon_code: couponCode, success });
}

// --- Credits ---

export function trackCreditsUsed(feature: string, cost: number) {
  trackEvent("credits_used", { feature, cost });
}

// --- Engagement ---

export function trackFeatureUsed(featureName: string) {
  trackEvent("feature_used", { feature_name: featureName });
}

export function trackShareClicked(contentType: string) {
  trackEvent("share_clicked", { content_type: contentType });
}

export function trackModalOpened(modalName: string) {
  trackEvent("modal_opened", { modal_name: modalName });
}

export function trackModalClosed(modalName: string) {
  trackEvent("modal_closed", { modal_name: modalName });
}
